import * as React from 'react';
import clsx from 'clsx';
import { Textarea } from '@/components/ui';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import type { PublicChatTheme } from '../themes/types';

import { VoiceButton } from '@/components/chat/input/VoiceButton';
import { RecordingInterface } from '@/components/chat/input/RecordingInterface';
import { ChatInputShell } from '@/components/chat/input/ChatInputShell';
import { usePushToTalk } from '@/hooks/usePushToTalk';
import { useUiStore } from '@/store/uiStore';

interface PublicChatInputProps {
  onSend: (message: string) => void;
  onSendAudio?: (blob: Blob, waveform: number[], duration: number) => void;
  isLoading?: boolean;
  theme: PublicChatTheme;
}

const CRIMSON_BORDER_GRADIENT = 'linear-gradient(312.85deg, #6784FE 21%, #54FCF8 90.76%)';

/**
 * Public-chat (anonymous landing page) input. Same chrome + push-to-talk as
 * the logged-in `ChatInput`, themed via `PublicChatTheme` and without the
 * desktop glow / @mention dropdown.
 */
export const PublicChatInput: React.FC<PublicChatInputProps> = ({
  onSend,
  onSendAudio,
  isLoading,
  theme,
}) => {
  const [message, setMessage] = React.useState('');
  const [isSending, setIsSending] = React.useState(false);
  const openModal = useUiStore((state) => state.openModal);

  const {
    isRecording,
    recordingTime,
    audioData,
    startRecording,
    stopRecording,
    cancelRecording,
    handleTouchStart,
  } = usePushToTalk({
    ...(onSendAudio !== undefined ? { onSendAudio } : {}),
    disabled: isLoading || isSending,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || isLoading || isSending) return;

    setIsSending(true);
    onSend(trimmed);
    setMessage('');
    setIsSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  const borderGradient = theme.name === 'crimson' ? CRIMSON_BORDER_GRADIENT : undefined;

  // Voice input is disabled in public chat for now; force the send icon to always show.
  const hasText = true;

  return (
    <ChatInputShell
      onSubmit={handleSubmit}
      hideGlow
      expandHeight={isRecording}
      {...(borderGradient !== undefined ? { borderGradient } : {})}
      innerClassName={theme.input.wrapper}
      disclaimer={
        !isRecording && (
          <p
            className={clsx(
              'mx-auto mt-3 max-w-[92%] text-center font-sans text-10 font-medium leading-160 xl:text-11',
              theme.text.secondary
            )}
          >
            This is a conversation powered by artificial intelligence. Echoes can make mistakes.
            Check important info. See{' '}
            <button
              type='button'
              onClick={() => openModal('echoDetails')}
              className='font-bold underline decoration-solid decoration-auto transition-colors hover:text-white/80'
            >
              Echoes Details
            </button>
            .
          </p>
        )
      }
    >
      {isRecording ? (
        <RecordingInterface
          recordingTime={recordingTime}
          audioData={audioData}
          onStop={async () => {
            const result = await stopRecording();
            if (result && onSendAudio) {
              onSendAudio(result.blob, audioData, result.duration);
            }
          }}
          onCancel={cancelRecording}
        />
      ) : (
        <>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Enter Message...'
            className={clsx(
              'size-full resize-none',
              'border-none bg-transparent shadow-none',
              'font-sans text-16 font-medium leading-160 tracking-[-0.02em]',
              theme.input.field,
              'py-[20px] pl-[30px] pr-[120px]',
              'focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0',
              'disabled:opacity-50'
            )}
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              height: '68px',
            }}
            disabled={isLoading || isSending}
            autoFocus
          />

          {hasText ? (
            <button
              type='submit'
              disabled={isLoading || isSending}
              className={clsx(
                'absolute right-6 top-1/2 -translate-y-1/2 transition-colors disabled:opacity-50',
                theme.input.button
              )}
            >
              <PaperAirplaneIcon className='size-6' />
            </button>
          ) : (
            <VoiceButton
              onClick={startRecording}
              onTouchStart={handleTouchStart}
              disabled={isLoading || isSending}
            />
          )}
        </>
      )}
    </ChatInputShell>
  );
};
